import React from 'react';
import { Product } from '../../types';
import { Image } from '../common/Image';

interface ProductScreenshotsProps {
  screenshots: Product['screenshots'];
  productName: string;
  accentColor?: string;
}

export const ProductScreenshots: React.FC<ProductScreenshotsProps> = ({
  screenshots,
  productName,
  accentColor = '#D8A93C',
}) => {
  if (!screenshots || screenshots.length === 0) {
    return null;
  }

  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalImg, setModalImg] = React.useState<{src: string, alt: string} | null>(null);

  const openModal = (src: string, alt: string) => {
    setModalImg({ src, alt });
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  // Group by device (if any screenshot specifies a device); otherwise render flat.
  const hasDevice = screenshots.some((s) => !!s.device);
  const deviceLabels: Record<NonNullable<Product['screenshots'][number]['device']>, string> = {
    ipad: 'iPad',
    iphone: 'iPhone',
    mac: 'Mac',
  };
  const deviceOrder: Array<NonNullable<Product['screenshots'][number]['device']>> = ['ipad', 'iphone', 'mac'];
  const groups = hasDevice
    ? deviceOrder
        .map((device) => ({
          device,
          label: deviceLabels[device],
          items: screenshots.filter((s) => s.device === device),
        }))
        .concat(
          screenshots.some((s) => !s.device)
            ? [{ device: 'ipad', label: 'Other', items: screenshots.filter((s) => !s.device) }]
            : []
        )
        .filter((g) => g.items.length > 0)
    : [{ device: undefined as never, label: '', items: screenshots }];

  let globalIndex = 0;
  const renderItem = (screenshot: Product['screenshots'][number], index: number) => {
    const i = globalIndex++;
    return (
      <div key={`${screenshot.url}-${index}`} className="flex flex-col">
        <button
          className="bg-panelGray rounded-xl overflow-hidden border border-[#222] transition-all shadow-lg hover:scale-[1.02] hover:shadow-xl focus:outline-none"
          style={{ padding: 0, borderColor: undefined }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = accentColor)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#222')}
          onClick={() => openModal(screenshot.url, screenshot.alt || `${productName} screenshot ${i + 1}`)}
        >
          <Image
            src={screenshot.url}
            alt={screenshot.alt || `${productName} screenshot ${i + 1}`}
            width={screenshot.width}
            height={screenshot.height}
            loading={i < 2 ? 'eager' : 'lazy'}
            className="w-full h-auto aspect-[16/10] object-cover"
            style={{ maxHeight: '340px', minHeight: '220px' }}
          />
        </button>
        <p className="text-[0.95rem] opacity-40 mt-2 text-center font-mono">
          {screenshot.alt || `Screenshot ${i + 1}`}
        </p>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-12">
        {groups.map((group, gi) => (
          <div key={gi}>
            {hasDevice && group.label && (
              <h3
                className="text-sm font-mono tracking-[0.2em] mb-6 opacity-60"
                style={{ color: accentColor }}
              >
                {group.label}
              </h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {group.items.map((s, i) => renderItem(s, i))}
            </div>
          </div>
        ))}
      </div>
      {modalOpen && modalImg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={closeModal}>
          <img
            src={modalImg.src}
            alt={modalImg.alt}
            className="max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl border-4"
            style={{ borderColor: accentColor }}
            onClick={e => e.stopPropagation()}
          />
          <button className="absolute top-8 right-8 text-white text-3xl font-bold" onClick={closeModal}>&times;</button>
        </div>
      )}
    </>
  );
};
